// ===========================================
// Events React Query Hooks (Parent-facing)
// ===========================================
// Custom hooks for authenticated parents to view events,
// submit RSVPs, and purchase tickets.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { calendarKeys } from './useHybridBooking';
import { isSoldOutError } from '../utils/soldOut';
import {
  eventParentApi,
  ParentRsvpData,
  ParentTicketCheckoutData,
  MyEventTicket,
  MyRsvp,
} from '../api/events.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const parentEventKeys = {
  all: ['parentEvents'] as const,
  lists: () => [...parentEventKeys.all, 'list'] as const,
  list: (
    params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
  ) => [...parentEventKeys.lists(), params] as const,
  details: () => [...parentEventKeys.all, 'detail'] as const,
  detail: (id: string) => [...parentEventKeys.details(), id] as const,
  myTickets: (eventId: string) => [...parentEventKeys.all, 'myTickets', eventId] as const,
  myRsvp: (eventId: string) => [...parentEventKeys.all, 'myRsvp', eventId] as const,
};

// ===========================================
// SHARED ERROR TYPE
// ===========================================

type ApiError = Error & { response?: { data?: { message?: string } } };

// ===========================================
// PARENT QUERY HOOKS
// ===========================================

/**
 * List PUBLISHED upcoming events visible to authenticated parents.
 */
export function useParentEvents(params?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) {
  return useQuery({
    queryKey: parentEventKeys.list(params),
    queryFn: () => eventParentApi.list(params),
  });
}

/**
 * Get a single PUBLISHED event by ID.
 */
export function useParentEvent(id: string) {
  return useQuery({
    queryKey: parentEventKeys.detail(id),
    queryFn: () => eventParentApi.get(id),
    enabled: !!id,
  });
}

/**
 * Fetch the authenticated parent's own PAID/CHECKED_IN tickets for one event.
 *
 * apiClient + the api method each strip one wrapper layer, so the hook receives
 * the bare MyEventTicket[] (no further .data unwrap needed by callers).
 *
 * @param eventId - The event to query tickets for.
 * @param enabled - Set false to skip the query (e.g. while the event is loading).
 */
export function useMyEventTickets(eventId: string, enabled = true): import('@tanstack/react-query').UseQueryResult<MyEventTicket[], Error> {
  return useQuery({
    queryKey: parentEventKeys.myTickets(eventId),
    queryFn: () => eventParentApi.getMyEventTickets(eventId),
    enabled: !!eventId && enabled,
    // Ticket status changes server-side at check-in (admin scans the QR), so the
    // parent's copy must stay fresh — otherwise the chip shows "Valid" after the
    // ticket has been used. Override the app-wide 5-min staleTime / no-focus-refetch.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * The family's own RSVP for one event (#253): `null` = not answered yet, which is
 * a fact; `undefined` = still loading; `isError` = could not be read. Callers must
 * tell the three apart and never render the blank form on loading or error.
 *
 * Always refetched on mount, so a prefill is never a stale answer.
 *
 * #272: parent-only. The one caller (ParentRsvpPanel) mounts after the event itself
 * has loaded, on the free, non-cancelled branch of the parent event page.
 */
export function useMyRsvp(eventId: string): import('@tanstack/react-query').UseQueryResult<MyRsvp | null, Error> {
  return useQuery({
    queryKey: parentEventKeys.myRsvp(eventId),
    queryFn: () => eventParentApi.getMyRsvp(eventId),
    enabled: !!eventId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// ===========================================
// PARENT MUTATION HOOKS
// ===========================================

/**
 * Submit or update an RSVP for the authenticated parent's family.
 *
 * familyId is resolved server-side from the session — do NOT include it in
 * the data object. See eventParent.routes.ts / resolveParentFamilyId().
 */
export function useSubmitRsvp() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: ParentRsvpData }) =>
      eventParentApi.rsvp(eventId, data),
    onSuccess: (rsvp, { eventId }) => {
      // The server's RETURNED row is the truth (a full event turns ATTENDING into
      // WAITLIST), so the cache takes it as is, then re-reads.
      queryClient.setQueryData(parentEventKeys.myRsvp(eventId), {
        id: rsvp.id,
        response: rsvp.response,
        guestCount: rsvp.guestCount,
        notes: rsvp.notes,
        updatedAt: rsvp.updatedAt,
      });
      queryClient.invalidateQueries({ queryKey: parentEventKeys.myRsvp(eventId) });
      queryClient.invalidateQueries({ queryKey: parentEventKeys.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: parentEventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar(
        rsvp.response === 'WAITLIST' ? 'This event is full: you are on the waitlist' : 'RSVP saved',
        { variant: rsvp.response === 'WAITLIST' ? 'warning' : 'success' }
      );
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to submit RSVP';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Initiate a Stripe Checkout session for ticket purchase.
 *
 * On success the mutation returns { sessionId, url }.
 * Callers should redirect to `result.url` immediately after the mutation
 * resolves:
 *
 *   const checkout = useTicketCheckout();
 *   checkout.mutate({ eventId, data }, {
 *     onSuccess: ({ url }) => { window.location.href = url; },
 *   });
 */
export function useTicketCheckout() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string;
      data: ParentTicketCheckoutData;
    }) => eventParentApi.checkout(eventId, data),
    onError: (error: ApiError, variables) => {
      const message = error.response?.data?.message || 'Failed to initiate checkout. Please try again.';
      enqueueSnackbar(message, { variant: 'error' });
      // SOLD_OUT (by its CODE, B3b #687): the seats left changed, so re-read the event; the page then shows
      // "Sold out" or the smaller limit instead of letting the buyer try again.
      if (isSoldOutError(error)) {
        void queryClient.invalidateQueries({ queryKey: parentEventKeys.detail(variables.eventId) });
      }
    },
  });
}
